<script lang="ts" setup>
import { computed, ref } from 'vue'
import { Dropdown as VDropdown } from 'floating-vue'
import { cva } from 'class-variance-authority'
import { useMe } from '@/queries/me'
import useUtils from '@/store/utils'
import { PhCaretDown, PhSignOut, PhUser } from '@phosphor-icons/vue'
import { useModalTriggers } from '@/composables/modals'
import useTithelyNavSettings from '@/composables/nav/trinitySettings'
import TrinityNavIconLink from '@/layouts/components/TrinityNavIconLink.vue'
import { usePerson } from '@/queries/people/person'
import { isEmpty, startsWith } from 'lodash'
import ENV from '@/constants/env'

const utils = useUtils()
const { filteredNav } = useTithelyNavSettings()
const orgNav = filteredNav(`userAndOrganization`)
const userNav = filteredNav(`account`)
const { data: me } = useMe()
const { handleModalAction } = useModalTriggers()

const extraDropdown = ref(null)
const open = ref(false)

const firstName = computed(() => me.value?.firstName ?? ``)
const lastName = computed(() => me.value?.lastName ?? ``)

// Profile Picture
const linkedPersonId = computed(() => me.value?.linkedPersonId)
const hasLinkedPerson = computed(() => !!linkedPersonId.value)
const { data: profile } = usePerson(linkedPersonId, hasLinkedPerson)
const profilePicture = computed(() => {
    const url = profile.value?.personDetails?.profilePicture
    if (!url) {
        return null
    }

    if (startsWith(url, 'img/profiles/upload')) {
        return `${ENV.fileHost}/${url}`
    }

    // If not a normal uploaded picture, unsure what to do.
    return null
})

const hasOrgNav = computed(() => !isEmpty(orgNav))

const dropdownProps = computed(() => ({
    triggers: [`click`],
    placement: `bottom-end`,
    distance: 9,
    theme: `main-nav-settings`,
    popperClass: `horizon-ui`,
    autoHide: true,
    positioningDisabled: utils.getMaxWidth(`md`),
    strategy: `fixed`
}))

const containerClasses = cva(
    `flex h-10 items-center justify-center !space-x-2 rounded-full bg-primary !px-2 transition duration-200 ease-in-out`,
    {
        variants: {
            open: {
                true: `text-white`,
                false: `bg-opacity-20 text-primary hover:bg-opacity-30`
            }
        }
    }
)

const profileClasses = cva(`overflow-hidden rounded-full`, {
    variants: {
        open: {
            true: `bg-white text-primary`,
            false: `bg-primary text-white`
        }
    }
})

const caretClasses = cva([`!ml-1`, `size-4`, `transition-transform`, `duration-200`, `ease-in-out`], {
    variants: {
        open: {
            true: `rotate-180`,
            false: `rotate-0`
        }
    }
})

const action = (hide: () => void, action?: string) => {
    hide()

    if (action) {
        handleModalAction(action)
    }
}
</script>

<template>
    <div
        ref="extraDropdown"
        class="!ml-4">
        <VDropdown
            v-bind="dropdownProps"
            v-model:shown="open">
            <button
                type="button"
                :class="containerClasses({ open })">
                <div
                    v-if="profilePicture"
                    class="overflow-hidden rounded-full bg-primary">
                    <img
                        :src="profilePicture"
                        class="size-6 object-cover" />
                </div>
                <div
                    v-else
                    :class="profileClasses({ open })">
                    <PhUser
                        class="size-6"
                        weight="duotone" />
                </div>
                <PhCaretDown
                    weight="bold"
                    :class="caretClasses({ open })" />
            </button>

            <template #popper="{ hide }">
                <div class="w-[30rem]">
                    <header
                        v-if="hasOrgNav"
                        class="!p-4">
                        <h3 class="!my-0 font-sans text-base/normal font-medium tracking-[-2%]">
                            {{ $t(`nav.usersAndOrganization`) }}
                        </h3>
                    </header>

                    <ul
                        v-if="hasOrgNav"
                        class="!my-2 flex flex-col gap-2">
                        <li
                            v-for="(link, index) in orgNav"
                            :key="`OrgNav${index}`">
                            <TrinityNavIconLink
                                v-bind="link"
                                @action="action(hide, $event)" />
                        </li>
                    </ul>

                    <header class="!p-4">
                        <h3 class="!my-0 font-sans text-base/normal font-medium tracking-[-2%]">
                            {{ $t(`nav.manageYourAccount`) }}
                        </h3>
                        <p class="tracking-[-2%]l !my-0 font-sans text-sm/normal font-normal">
                            {{ firstName }} {{ lastName }}
                        </p>
                    </header>

                    <ul class="!my-2 flex flex-col gap-2">
                        <li
                            v-for="(link, index) in userNav"
                            :key="`AccountNav${index}`">
                            <TrinityNavIconLink
                                v-bind="link"
                                @action="action(hide, $event)" />
                        </li>
                        <li>
                            <a
                                :href="`/login/logout?tithely_ui_redirect=${encodeURIComponent('/logout')}`"
                                class="flex items-center gap-2 !p-2 hover:bg-primary/10">
                                <div class="flex w-12 shrink-0 grow-0 items-center justify-center">
                                    <PhSignOut
                                        weight="duotone"
                                        class="size-6 text-primary" />
                                </div>
                                <div class="flex flex-col items-start justify-center">
                                    <h4
                                        class="!my-0 font-sans text-base/normal font-medium tracking-[-2%] text-primary">
                                        {{ $t(`nav.logout`) }}
                                    </h4>
                                </div>
                            </a>
                        </li>
                    </ul>
                </div>
            </template>
        </VDropdown>
    </div>
</template>

<style lang="scss">
.v-popper {
    &--some-open--main-nav-settings {
        @apply overflow-auto;
    }

    &--theme-main-nav-settings {
        &.v-popper__popper {
            @apply fixed;
        }

        .v-popper {
            &__wrapper {
                @apply overflow-hidden rounded-lg bg-white shadow-lg;
            }

            &__arrow-container {
                @apply absolute z-[1] m-0.5 size-3 border-[6px] border-solid border-transparent before:border before:shadow-lg md:block;
                @apply before:relative before:block before:size-3 before:rotate-45 before:border-solid before:border-transparent before:bg-white;
            }
        }

        &[data-popper-placement='bottom-end'] {
            .v-popper__arrow-container {
                @apply -bottom-1.5 left-[calc(50%-12px)] my-0 border-b-0 border-t-white before:-left-2 before:bottom-3;
            }
        }
    }
}
</style>
